import {Pipe, PipeTransform} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';

/**
 * The VimeoUrlPipe can be used to modify the video URL to match the needs of the embedded Vimeo video player.
 * This pipe validates that URLs are from Vimeo before sanitizing them for security.
 */
@Pipe({name: 'vimeoUrl'})
export class VimeoUrlPipe implements PipeTransform {
  private readonly VIMEO_DOMAIN_PATTERN = /^https:\/\/(www\.)?vimeo\.com\//;
  private readonly VIMEO_PLAYER_PATTERN = /^https:\/\/player\.vimeo\.com\/video\//;

  constructor(protected sanitizer: DomSanitizer) {}

  transform(value: any, ...args: any[]): SafeResourceUrl | null {
    if (!value || typeof value !== 'string') {
      return null;
    }

    // Validate that the URL is from Vimeo (security check)
    if (!this.isValidVimeoUrl(value)) {
      console.warn('Invalid Vimeo URL provided:', value);
      return null;
    }

    let sanitizedUrl = value;

    // Transform vimeo.com URLs to player.vimeo.com format
    if (this.isNeedToTransform(value)) {
      sanitizedUrl = value.replace('vimeo.com/', 'player.vimeo.com/video/');
      const urlParts: string[] = sanitizedUrl.split('/');
      sanitizedUrl = urlParts.slice(0, 5).join('/');
    }

    // Only bypass security after validating it's a legitimate Vimeo URL
    return this.sanitizer.bypassSecurityTrustResourceUrl(sanitizedUrl); // NOSONAR
  }

  /**
   * Validates that the URL is from a legitimate Vimeo domain.
   * This prevents arbitrary URLs from being embedded.
   */
  private isValidVimeoUrl(value: string): boolean {
    try {
      const url = new URL(value);
      // Only allow https URLs from vimeo.com or player.vimeo.com
      return (
        url.protocol === 'https:' && (this.VIMEO_DOMAIN_PATTERN.test(value) || this.VIMEO_PLAYER_PATTERN.test(value))
      );
    } catch {
      return false;
    }
  }

  private isNeedToTransform(value: string): boolean {
    return !value.includes('player.vimeo.com/video');
  }
}
